import React, { useState } from "react";
import {
  Box,
  Button,
  FormField,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import { normaliseAlias } from "./AssigneeAutosuggest";
import { OwnerSelect } from "./OwnerSelect";

interface AssignNeedsOwnerModalProps {
  stage: Stage;
  investigation: Investigation;
  currentUser: string | null;
  onDismiss: () => void;
  /** `alias` is omitted when the viewer takes it themselves. */
  onConfirm: (alias?: string) => void;
}

/**
 * Confirmation behind a table's "Needs an owner" badge: the same owner picker as the
 * investigation page, preset to the viewer; picking someone else assigns it to them instead.
 */
export const AssignNeedsOwnerModal: React.FC<AssignNeedsOwnerModalProps> = ({
  stage,
  investigation,
  currentUser,
  onDismiss,
  onConfirm,
}) => {
  const me = currentUser ? normaliseAlias(currentUser) : "";
  const [alias, setAlias] = useState(me);
  const isMe = !!alias && alias === me;

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header={isMe || !alias ? "Take ownership?" : "Assign owner?"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!alias}
              onClick={() => {
                onConfirm(isMe ? undefined : alias);
                onDismiss();
              }}
            >
              {isMe || !alias ? "Take ownership" : `Assign to ${alias}`}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <Box>“{investigation.title}” needs an owner.</Box>
        <FormField label="Owner">
          <OwnerSelect
            stage={stage}
            value={alias || null}
            onChange={(a) => setAlias(normaliseAlias(a))}
            pinned={me ? [{ alias: me, description: "Me" }] : []}
            placeholder="Choose an owner"
            loadImmediately
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default AssignNeedsOwnerModal;
